import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { reset, stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { FIELDS_FOR_VALIDATE, FORM_NAME, REQUEST_ERROR_MESSAGE } from './constants';
import { transformToValidationErrors } from './utils';
import { DISMISS_TIME } from '../../api/constants';
import {
	CREATE_ORDERS_WAYS,
	DELETE_ORDERS_WAYS,
	GET_ORDERS_WAYS_INFO,
	GET_GROUP_ID_LIST,
	setIsOrdersWaysDeletingAction,
	setIsOrdersWaysInfoLoadingAction,
	setIsOrdersWaysSavingAction,
	setIsCarriersListLoadingAction,
	setOrdersWaysInfoAction,
	setGroupIdListAction,
	UPDATE_ORDERS_WAYS,
	getOrdersWaysInfoAction,
} from './actions';
import {
	createOrdersWaysItemRequest,
	deleteOrdersWaysRequest,
	getOrdersWaysRequest,
	getOrdersGroupsListRequest,
	updateOrdersWaysRequest,
} from '../../api/requests';
import { getDataForCreateOrUpdateOrdersWaysSelector, getFormIsInvalidSelector } from './selectors';
import {
	getGroupsWithWaysListAction,
	getCollectionGroupListAction,
	getWaysGroupListAction,
	getWaysListAction,
	getGroupsListAction,
} from '../orders-groups/actions';
import { errorHandler } from '../../api/utils';
import { getGroupOpenSelector, getGroupWayOpenSelector } from '../orders-groups/selectors';
import { getOrdersListAction } from '../orders/actions';

export function* getOrdersWaysInfoSaga({ id }) {
	try {
		yield put(setIsOrdersWaysInfoLoadingAction(true));

		const { data, message, toast } = yield call(getOrdersWaysRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setOrdersWaysInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrdersWaysInfoLoadingAction(false));
	}
}

export function* deleteOrdersWaysSaga({ payload: { id, redirect, searchQuery, pathname } }) {
	try {
		yield put(setIsOrdersWaysDeletingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const { data, message, toast } = yield call(deleteOrdersWaysRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			if (pathname.includes('orders_groups')) {
				yield put(getGroupsListAction({ searchQuery, noLoading: true }));
				yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));
				yield put(getWaysListAction());

				if (groupWayOpen) {
					yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
				}

				if (groupOpen) {
					yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));
				}
			} else if (pathname.includes('orders')) {
				yield put(getOrdersListAction({ searchQuery, noLoading: true }));
			}

			redirect();
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrdersWaysDeletingAction(false));
	}
}

export function* createOrdersWaysSaga({ payload: { redirect, resetState, searchQuery } }) {
	try {
		yield put(setIsOrdersWaysSavingAction(true));

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForCreateOrUpdateOrdersWaysSelector());

		const { data, errors, message, toast } = yield call(createOrdersWaysItemRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));
			yield put(getWaysListAction());

			if (groupWayOpen && groupWayOpen.id === data?.group.id) {
				yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
			}

			redirect();
			resetState();
			reset(FORM_NAME);
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrdersWaysSavingAction(false));
	}
}

export function* updateOrdersWaysSaga({ payload: { id, redirect, searchQuery, pathname } }) {
	try {
		yield put(setIsOrdersWaysSavingAction(true));

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForCreateOrUpdateOrdersWaysSelector());

		const { data, errors, message, toast } = yield call(updateOrdersWaysRequest, id, dataForSave);
		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(setOrdersWaysInfoAction(data));

			if (pathname.includes('orders_groups')) {
				yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));
				yield put(getWaysListAction());

				if (groupWayOpen) {
					yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
				}

				yield put(getOrdersWaysInfoAction(id));
			} else if (pathname.includes('orders')) {
				yield put(getOrdersListAction({ searchQuery, noLoading: true }));
			}

			redirect();
			reset(FORM_NAME);
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrdersWaysSavingAction(false));
	}
}

export function* getOrdersWaysListSaga() {
	try {
		yield put(setIsCarriersListLoadingAction(true));
		const { data, message, toast } = yield call(getOrdersGroupsListRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setGroupIdListAction(
					data?.map(carrier => ({
						text: carrier.name,
						value: carrier.id,
						key: `${carrier.name.trim()}-${carrier.id}`,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCarriersListLoadingAction(false));
	}
}

export default function* ordersWaysModalSaga() {
	yield takeEvery(GET_ORDERS_WAYS_INFO, getOrdersWaysInfoSaga);
	yield takeEvery(DELETE_ORDERS_WAYS, deleteOrdersWaysSaga);
	yield takeEvery(CREATE_ORDERS_WAYS, createOrdersWaysSaga);
	yield takeEvery(UPDATE_ORDERS_WAYS, updateOrdersWaysSaga);
	yield takeEvery(GET_GROUP_ID_LIST, getOrdersWaysListSaga);
}
